import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Shield, ShieldOff, Pencil, Trash2, X, Plus } from "lucide-react";
import {
  listMembers,
  createMember,
  updateMember,
  deleteMember,
  makeMemberAdmin,
  type Member,
} from "@/lib/members-api";
import { fmtDate } from "@/lib/format-date";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "@/lib/phone";

export const Route = createFileRoute("/admin/members")({ component: Page });

/** Time-only (HH:MM) from an ISO timestamp; empty when unparseable. */
function fmtTime(input: string | null | undefined): string {
  if (!input) {
    return "";
  }
  const d = new Date(input);
  if (isNaN(d.getTime())) {
    return "";
  }
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

type CreateDraft = {
  full_name: string;
  email: string;
  phone: string;
  isAdmin: boolean;
};
const EMPTY_CREATE: CreateDraft = {
  full_name: "",
  email: "",
  phone: "",
  isAdmin: false,
};

function Page() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Member | null>(null);
  const [create, setCreate] = useState<CreateDraft | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-members"],
    queryFn: listMembers,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-members"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: CreateDraft) =>
      createMember({
        full_name: d.full_name,
        email: d.email,
        phone: d.phone,
        role: d.isAdmin ? "Admin" : "Member",
        is_staff: d.isAdmin,
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Member created");
      setCreate(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (m: Member) =>
      updateMember(m.id, {
        full_name: m.full_name ?? "",
        phone: m.phone ?? "",
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Member updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMember(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });
  const makeAdminMut = useMutation({
    mutationFn: (id: string) => makeMemberAdmin(id),
    onSuccess: () => {
      invalidate();
      toast.success("Promoted to admin");
    },
    onError,
  });

  // Latest first — newest registration at the top.
  const sorted = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const del = (id: string) => {
    if (confirm("Delete this member?")) {
      deleteMut.mutate(id);
    }
  };

  const toggleAdmin = (m: Member) => {
    if (m.isAdmin) {
      // No "remove admin" endpoint provided yet.
      toast("Remove-admin API not connected yet");
      return;
    }
    makeAdminMut.mutate(m.id);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-bold">Members</h2>
          <p className="text-sm text-muted-foreground">{list.length} registered devotees</p>
        </div>
        <button onClick={() => setCreate({ ...EMPTY_CREATE })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Create Member
        </button>
      </div>
      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Member</th>
                <th className="p-3">Email</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Progress</th>
                <th className="p-3">Date</th>
                <th className="p-3">Time</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    Loading members…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load members"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    No members yet
                  </td>
                </tr>
              )}
              {sorted.map((m) => (
                <tr key={m.id} className="border-t border-border hover:bg-muted/40">
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-primary-foreground"
                        style={{ background: "var(--gradient-saffron)" }}
                      >
                        {(m.full_name || m.email || m.phone || "Y")[0].toUpperCase()}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold">{m.full_name || "Unnamed"}</span>
                        {m.isAdmin && (
                          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {m.email ? (
                      <a href={`mailto:${m.email}`} className="text-primary hover:underline">
                        {m.email}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">{m.phone || "—"}</td>
                  <td className="p-3">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">
                      {m.visited}/{m.total}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {fmtDate(m.created_at) || "—"}
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {fmtTime(m.created_at) || "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => toggleAdmin(m)}
                        disabled={makeAdminMut.isPending}
                        title={m.isAdmin ? "Remove admin" : "Make admin"}
                        className="rounded-lg p-1.5 hover:bg-muted disabled:opacity-60"
                      >
                        {m.isAdmin ? (
                          <ShieldOff className="h-4 w-4" />
                        ) : (
                          <Shield className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setEdit(m)}
                        title="Edit"
                        className="rounded-lg p-1.5 hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => del(m.id)}
                        title="Delete"
                        className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {create && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setCreate(null)}
        >
          <div
            className="mx-auto my-8 w-full max-w-md rounded-2xl bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-bold">Create Member</h3>
              <button onClick={() => setCreate(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <MField label="Full Name">
                <input
                  className="minp"
                  value={create.full_name}
                  onChange={(e) => setCreate({ ...create, full_name: e.target.value })}
                />
              </MField>
              <MField label="Email">
                <input
                  type="email"
                  className="minp"
                  value={create.email}
                  onChange={(e) => setCreate({ ...create, email: e.target.value })}
                />
              </MField>
              <MField label="Phone">
                <input
                  {...PHONE_INPUT_PROPS}
                  required
                  className="minp"
                  value={create.phone}
                  onChange={(e) => setCreate({ ...create, phone: sanitizePhone(e.target.value) })}
                />
              </MField>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={create.isAdmin}
                  onChange={(e) => setCreate({ ...create, isAdmin: e.target.checked })}
                />
                Grant admin access
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setCreate(null)}
                disabled={createMut.isPending}
                className="btn-outline py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => create && createMut.mutate(create)}
                disabled={
                  createMut.isPending || !create.full_name.trim() || !isValidPhone(create.phone)
                }
                className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
              >
                {createMut.isPending ? "Creating…" : "Create"}
              </button>
            </div>
            <style>{`.minp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
          </div>
        </div>
      )}

      {edit && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setEdit(null)}
        >
          <div
            className="mx-auto my-8 w-full max-w-md rounded-2xl bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-bold">Edit Member</h3>
              <button onClick={() => setEdit(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <MField label="Full Name">
                <input
                  className="minp"
                  value={edit.full_name ?? ""}
                  onChange={(e) => setEdit({ ...edit, full_name: e.target.value })}
                />
              </MField>
              <MField label="Phone">
                <input
                  {...PHONE_INPUT_PROPS}
                  required
                  className="minp"
                  value={edit.phone ?? ""}
                  onChange={(e) => setEdit({ ...edit, phone: sanitizePhone(e.target.value) })}
                />
              </MField>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setEdit(null)}
                disabled={updateMut.isPending}
                className="btn-outline py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => edit && updateMut.mutate(edit)}
                disabled={updateMut.isPending || !isValidPhone(edit.phone)}
                className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
              >
                {updateMut.isPending ? "Saving…" : "Save"}
              </button>
            </div>
            <style>{`.minp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function MField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
